// Кабинет руководителя — деньги, люди, сроки на одном экране.
//
// Этот экран заменяет «Мою команду» из exec-панели (`ЗНАНИЕ_из_exec_панели.md`). Оттуда
// взяты смысл и грабли, а данные — из ядра.
//
// ── Главное, что нужно знать, читая этот файл ──
//
// «Мои люди» и «моя зона ответственности» — ДВА НЕЗАВИСИМЫХ ИЗМЕРЕНИЯ, и они здесь нигде
// не складываются:
//   • блок «Деньги» считается ПО ОТВЕТСТВЕННОМУ в сделке/платеже (ASSIGNED_BY_ID);
//   • блок «Люди»  считается ПО ОРГСТРУКТУРЕ (UF_DEPARTMENT + UF_HEAD из /refs).
// Связи между ними в Bitrix нет (`ЗНАНИЕ_из_exec_панели.md` §2.1). Живой случай: у
// руководителя сервиса СПб 16 человек в отделе и ноль дебиторки, потому что деньги
// по Петербургу висят на координаторах чужого отдела. Одна общая цифра тут врала бы.
// Поэтому разделение не только в коде — оно написано словами на самом экране.
//
// ── Экран ТОЛЬКО ЧИТАЮЩИЙ ──
// Ни одной записи в Bitrix. Это раздел-обзор: он собирает и ведёт в существующие разделы,
// а действие совершается там, где ему место (в CRM или, пока живы бизнес-процессы, в Bitrix).
//
// ── Своих формул по деньгам нет ──
// Долг и просрочка — lib/payments.ts (тот же код, что в «Дебиторке»). Оргструктура и
// пороги «горит» — lib/dashboard.ts. Здесь только разметка и сборка.
//
// ── Про полноту ──
// Ядро отдаёт страницами по 50 и агрегатов не умеет. Точны только СЧЁТЧИКИ (`total` ядро
// считает отдельным SQL по всей выборке). Любая СУММА и любое распределение «по людям»
// посчитаны по загруженной странице — и везде, где это так, под цифрой написано «по N из M».
//
// ИСКЛЮЧЕНИЕ, которое надо знать: блок «Задания процессов» устроен наоборот. `/bp/by-user`
// — это готовый агрегат ядра по ВСЕМУ зеркалу заданий, без постраничности. Там «по N из M»
// не пишется не по забывчивости: числа точные, и на экране это сказано прямо, потому что
// иначе человек перенесёт на них недоверие к соседним плиткам.
//
// ── Три источника, три разные границы ──
// Сделки и смарт-процессы — зеркало Bitrix, возраст ~5 минут. Задания процессов — зеркало
// bizproc, и в нём ЖИВЫЕ задания полны, а закрытые нет: наблюдать начали 27.07.2026.
// Разрывы цепочки — окно в 90 дней по дате создания сделки и границы каждого источника
// внутри (АВР с 20.02.2024, платежи с 16.04.2024, счета с 25.04.2023). Возраст и окно
// показаны в самих блоках: цифра без границы — это обвинение без основания.

import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useЗадания, useПоЛюдям } from '../api/bp'
import type { ЗаданиеСтрока } from '../api/bp'
import { useРазрывы } from '../api/chain'
import { ОКНА } from '../lib/chain'
import {
  useCompanyTitles, useDeals, useItems, usePayments, useRefs, useRefsQuery, useRightsApi,
} from '../api/hooks'
import { useSelf } from '../api/self'
import type { Deal, ListParams } from '../api/types'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { видЗадания, видОжидания, названиеДокумента, ссылкаВBitrix } from '../lib/bp'
import {
  HANG_DAYS, HOT_ROWS, INVOICE_PAID_CODES, КЛЮЧЕВЫЕ_РАЗРЫВЫ, PAGE, REPORT_HANG_CODES, STUCK_DAYS,
  bpИтогПоЛюдям, bpПоId, bpЧеловек, countByAssignee, daysAgoIso, daysSince, idleDays, makeOrg,
  monthRange, plural, разрывыПоВидам, разрывыРубли, stageIdsByCode, sumDeals,
} from '../lib/dashboard'
import type { BpПоЛюдям, RefDepartment, RefsOrgData } from '../lib/dashboard'
import { fmtDate, fmtDays, fmtMoney, pluralN, todayIso } from '../lib/format'
import { PAID_CATEGORY_ID, PAY, PAY_LIST_SELECT, paymentView, summarize } from '../lib/payments'
import { SYSTEM_SELECT, bitrixItemUrl, refId } from '../lib/smart'

/** Счета клиенту — смарт-процесс 31. Отчёты по сделкам (цепочка согласований) — 176. */
const INVOICE_TYPE = 31
const REPORT_TYPE = 176

/** Сколько строк людей показываем. У корня оргструктуры подчинённых 382 — все не нужны. */
const TEAM_ROWS = 30

/**
 * Окно разрывов цепочки, в днях по дате создания сделки. 90 — умолчание самого ядра
 * (`chain_api.ОКНО_ДНЕЙ`), и менять его здесь нельзя без причины: раздел «Путь сделки»
 * показывает те же разрывы за то же окно, и два экрана обязаны отвечать одинаково.
 */
const GAP_DAYS = 90

/**
 * Сколько строк разрывов просим у ядра. 100, а не 50: по этой странице считаются числа
 * «сколько разрывов какого вида», и чем чаще страница вмещает ВСЮ выборку, тем чаще эти
 * числа точные, а не «по N из M». Замер 28.07.2026 на боевом зеркале: у самого нагруженного
 * ответственного за 90 дней 83 сделки с разрывом, у остальных из первой шестёрки — 44…77.
 * То есть сотне хватает всем; когда не хватит, экран так и скажет.
 */
const GAP_ROWS = 100

/**
 * Порог «задание висит долго», в днях. Тот же, что по умолчанию в ядре (`bp_api.СТАРО_ДНЕЙ`)
 * и в разделе «Задания процессов»: числа `old` в ответе `/bp/by-user` посчитаны именно по
 * нему, и подставить сюда другое значило бы подписать чужую цифру своим порогом.
 */
const BP_OLD_DAYS = 30

/** Поля сделки, которые нужны этому экрану. Всё остальное не просим — страница и так на 50 строк. */
const DEAL_SELECT = ['ID', 'TITLE', 'STAGE_ID', 'OPPORTUNITY', 'CURRENCY_ID', 'COMPANY_ID',
                     'ASSIGNED_BY_ID', 'CLOSED', 'DATE_MODIFY', 'CLOSEDATE']

export default function Dashboard() {
  const [sp, setSp] = useSearchParams()
  const я = useSelf()
  // Сотрудник живёт в адресе (?user=123), как компания в Client360: ссылку на срез
  // конкретного руководителя можно переслать, и она откроется на нём же. Но адрес — это
  // ПЕРЕОПРЕДЕЛЕНИЕ, а умолчание теперь даёт сессия: у учётки есть привязка к сотруднику
  // Bitrix (/api/me), и кабинет открывается сразу на себе. Роли user адрес не слушаем:
  // чужой срез ей не положен, сервер всё равно сузил бы ответ до её зоны, и экран,
  // притворяющийся «глазами коллеги», показал бы огрызок под чужим именем.
  const userId = я.canPickOthers ? (sp.get('user') || я.id) : я.id
  // Окно разрывов — тоже в адресе, и по той же причине: «покажи, что у него встало за год»
  // должно пересылаться ссылкой. Значение проверяем по списку: чужое число в адресе ядро
  // встретит ошибкой 400, и экран покажет отказ там, где человек ничего не выбирал.
  const gapDays = ОКНА.some((о) => String(о.дней) === sp.get('gapdays'))
    ? Number(sp.get('gapdays'))
    : GAP_DAYS

  const refs = useRefs()

  const change = (v: string) => {
    const next = new URLSearchParams(sp)
    if (v) next.set('user', v)
    else next.delete('user')
    setSp(next, { replace: true })
  }

  const changeDays = (v: string) => {
    const next = new URLSearchParams(sp)
    if (v && Number(v) !== GAP_DAYS) next.set('gapdays', v)
    else next.delete('gapdays')
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Кабинет руководителя</h1>
        <div className="spacer" />
        {/* Ссылки «все его сделки» здесь нет намеренно: раздел «Сделки» читает из адреса
            только компанию (`?company=`), фильтр по ответственному живёт в его локальном
            состоянии. Ссылка вида `?assigned=…` открыла бы НЕОТФИЛЬТРОВАННЫЙ список —
            то есть тихо соврала бы. Появится параметр в «Сделках» — появится и ссылка. */}
        <Link to="/receivables">раздел «Дебиторка» →</Link>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="dashboard">
          {/* Выбор сотрудника — только ролям, которым положено смотреть чужое (head/admin).
              У роли user селекта нет: «кто я» приходит из сессии, и вопрос снят. Это
              подсказка, а не защита — выборку сужает сервер по своей зоне. */}
          {я.canPickOthers && (
            <div className="f" style={{ minWidth: 320 }}>
              <label htmlFor="dash-user">Смотрим глазами сотрудника</label>
              {/* Обычный список, а не EntityPicker: сотрудников 926 (на 28.07.2026), это не
                  74 тысячи сделок — список грузится одним запросом справочников, который
                  экрану нужен всё равно. */}
              <select id="dash-user" value={userId} onChange={(e) => change(e.target.value)}
                      disabled={refs.users.length === 0}>
                <option value="">— выберите сотрудника —</option>
                {refs.users.map((u) => (
                  <option key={u.id} value={u.id}>{u.active ? u.name : `${u.name} (уволен)`}</option>
                ))}
              </select>
            </div>
          )}
          {/* Окно относится ТОЛЬКО к блоку «Где встала работа» — так и подписано. Общим
              фильтром экрана его делать нельзя: деньги считаются по открытым сделкам без
              всякого окна, и «за месяц» рядом с ними означало бы совсем другое. */}
          <div className="f">
            <label htmlFor="dash-gapdays">Окно разрывов цепочки</label>
            <select id="dash-gapdays" value={String(gapDays)}
                    onChange={(e) => changeDays(e.target.value)}>
              {ОКНА.map((о) => (
                <option key={о.дней} value={о.дней}>{о.ярлык} ({о.дней} дн.)</option>
              ))}
            </select>
          </div>
        </FilterBar>
        {/* Подпись под фильтрами говорит, откуда взят человек. Без привязки — честно
            про временное положение (текст остаётся только у непривязанных учёток). */}
        {я.loaded && !я.id && (
          <div className="hint" style={{ marginTop: 10 }}>
            Учётная запись CRM не связана с сотрудником Bitrix, поэтому «кто я» приходится
            выбирать руками. Привязку выставляет владелец в учётке — появится, и кабинет
            будет открываться сразу на вас.
          </div>
        )}
        {!!я.id && userId === я.id && !(я.canPickOthers && sp.get('user')) && (
          <div className="hint" style={{ marginTop: 10 }}>
            Кабинет открыт на вас — по привязке учётки к сотруднику Bitrix.
            {я.canPickOthers && ' Выбор сверху — посмотреть глазами другого сотрудника.'}
          </div>
        )}
        {я.canPickOthers && refs.users.length === 0 && (
          <div className="hint" style={{ marginTop: 6 }}>
            Справочник сотрудников из ядра не пришёл — выбирать не из кого.
          </div>
        )}
      </div>

      {/* Тело — отдельный компонент: пока сотрудник не выбран, к ядру не уходит ни один
          запрос. Хуки нельзя вызывать по условию, а пустой фильтр здесь означал бы
          «отдай все 74 тысячи сделок». */}
      {userId
        ? <DashboardBody userId={userId} gapDays={gapDays} />
        : (
          <div className="card">
            <div className="muted">
              {я.canPickOthers ? (
                <>
                  Выберите сотрудника выше. Экран покажет три вещи: деньги, за которые он
                  отвечает как ответственный в сделках; людей, если он руководит отделами;
                  и то, что горит по срокам. Деньги и люди считаются по разным признакам и
                  намеренно не сводятся в одну цифру — почему, написано в блоках.
                </>
              ) : (
                <>
                  Ваша учётка не связана с сотрудником Bitrix, и кабинет не знает, чьи
                  цифры показывать. Показывать чьи-нибудь он не станет. Привязку выставляет
                  владелец в учётке — скажите ему.
                </>
              )}
            </div>
          </div>
        )}
    </>
  )
}

function DashboardBody({ userId, gapDays }: { userId: string; gapDays: number }) {
  const refs = useRefs()
  // Тот же запрос, что внутри useRefs (ключ кэша ['refs'] один) — второй раз в сеть
  // не уходит. Нужен ради `departments`: их lib/refs.ts не разбирает.
  const refsQ = useRefsQuery()
  // Двойное приведение — не лень, а граница ответственности: `departments` ядро уже отдаёт,
  // но в api/types.ts (единственное место, где описан контракт) их ещё нет, а этот файл
  // его не правит. Появятся там — приведение уйдёт, и TypeScript сам покажет, где что.
  const org = useMemo(
    () => makeOrg(refsQ.data as unknown as RefsOrgData | undefined), [refsQ.data],
  )

  const today = todayIso()
  const month = useMemo(() => monthRange(), [])
  const stuckSince = useMemo(() => daysAgoIso(STUCK_DAYS), [])
  const hangSince = useMemo(() => daysAgoIso(HANG_DAYS), [])

  // ─────────────── ДЕНЬГИ: по ответственному в сделке ───────────────

  // Открытые сделки сотрудника. Счётчик `total` ядро считает по всей выборке — он точный;
  // сумма считается по странице и подписана.
  const openDeals = useDeals(useMemo<ListParams>(() => ({
    filter: { ASSIGNED_BY_ID: userId, CLOSED: 'N' },
    // По убыванию суммы, а не по дате: сумма всё равно считается по одной странице, и пусть
    // в неё попадут КРУПНЫЕ сделки — тогда «портфель по 50 из {total}» ближе к правде, чем
    // портфель по полусотне последних правок. Сумма пуста (NULL, а не ноль — ноль тут
    // настоящий) у 13 сделок из 3 062 открытых, на 28.07.2026, и Postgres при DESC ставит
    // их первыми — на одного человека это ноль-одна строка.
    order: { OPPORTUNITY: 'DESC' },
    select: DEAL_SELECT,
  }), [userId]))

  // Закрытые за календарный месяц. Верхняя граница строгая («меньше первого числа
  // следующего»), потому что ядро приводит дату без времени к полуночи.
  const closedMonth = useDeals(useMemo<ListParams>(() => ({
    filter: {
      ASSIGNED_BY_ID: userId, CLOSED: 'Y',
      '>=CLOSEDATE': month.from, '<CLOSEDATE': month.toExcl,
    },
    order: { CLOSEDATE: 'DESC' },
    select: DEAL_SELECT,
  }), [userId, month]))

  // Дебиторка: отбор ровно тот же, что в разделе «Дебиторка» и в Client360 —
  // пустая дата оплаты и воронка не «Оплачено».
  const payments = usePayments(useMemo<ListParams>(() => ({
    filter: { assignedById: userId, [PAY.paidDate]: '', '!categoryId': PAID_CATEGORY_ID },
    order: { [PAY.dueDate]: 'ASC' },
    select: PAY_LIST_SELECT,
  }), [userId]))

  // Второй срез той же дебиторки — по полю «Ответственный за дебиторку».
  // Зачем отдельный запрос. В exec-панели ответственным за долг считается тройка
  // COALESCE(resp_deb, coord, manager), а не ответственный карточки
  // (`ЗНАНИЕ_из_exec_панели.md` §7.5). На живых данных эти два числа расходятся
  // (у одного координатора 87 платежей «по карточке» против 46 «по полю»), и показать
  // одно, умолчав про другое, значит выбрать удобную правду.
  const paymentsResp = usePayments(useMemo<ListParams>(() => ({
    filter: { [PAY.respDebt]: userId, [PAY.paidDate]: '', '!categoryId': PAID_CATEGORY_ID },
    order: { [PAY.dueDate]: 'ASC' },
    select: PAY_LIST_SELECT,
  }), [userId]))

  const openRows = useMemo(() => openDeals.data?.items ?? [], [openDeals.data])
  const portfolio = useMemo(() => sumDeals(openRows), [openRows])
  const closedRows = useMemo(() => closedMonth.data?.items ?? [], [closedMonth.data])
  // «Выиграно» — стадия со смыслом «успех» по справочнику ядра. Список выигрышных стадий
  // не зашиваем: семантику стадии в Bitrix заполняет сам портал, у неё один автор.
  const wonRows = useMemo(
    () => closedRows.filter((d) => refs.stageKind(d.STAGE_ID) === 'won'), [closedRows, refs],
  )
  const wonSum = useMemo(() => sumDeals(wonRows), [wonRows])

  const payRows = useMemo(
    () => (payments.data?.items ?? []).map((p) => paymentView(p, refs, today)),
    [payments.data, refs, today],
  )
  const payTotals = useMemo(() => summarize(payRows), [payRows])
  const payRespRows = useMemo(
    () => (paymentsResp.data?.items ?? []).map((p) => paymentView(p, refs, today)),
    [paymentsResp.data, refs, today],
  )
  const payRespTotals = useMemo(() => summarize(payRespRows), [payRespRows])

  // ─────────────── ЛЮДИ: по оргструктуре ───────────────

  const headRoots = useMemo(() => org.headRoots(userId), [org, userId])
  const memberOf = useMemo(() => org.memberOf(userId), [org, userId])
  const zone = useMemo(
    () => org.subtree(headRoots.map((d) => d.id)), [org, headRoots],
  )
  const teamIds = useMemo(() => org.peopleOf(zone).filter((id) => id !== userId), [org, zone])
  // Сам руководитель в «людях» показан отдельной строкой, а не внутри команды: в exec-панели
  // ровно на этом обожглись — личные 73 сделки руководителя светились как показатель команды
  // (`ЗНАНИЕ_из_exec_панели.md` §3.8 п.5).

  // Запросы по команде живут в отдельном компоненте <TeamBlock> ниже, и он рисуется
  // ТОЛЬКО когда команда есть. Так и должно быть: у useDeals нет `enabled`, а первая
  // версия этого экрана подставляла в «пустой» случай параметры личного запроса, чтобы
  // сэкономить обращение к ядру. На живых данных это дало ровно то, чего мы избегаем:
  // пока запрос по команде не ответил, `placeholderData` показывал ПРЕДЫДУЩИЙ ответ —
  // и под подписью «их открытых сделок» стояли 175 личных сделок руководителя вместо
  // одной командной. Экономия одного запроса не стоит подменённой цифры.

  // ─────────────── СРОКИ: что горит ───────────────

  // Личные сделки без движения. Отдельный запрос, а не фильтрация страницы открытых:
  // так `total` — точное число застоявшихся, а не «сколько нашлось среди первых 50».
  const stuckDeals = useDeals(useMemo<ListParams>(() => ({
    filter: { ASSIGNED_BY_ID: userId, CLOSED: 'N', '<=DATE_MODIFY': stuckSince },
    order: { DATE_MODIFY: 'ASC' },
    select: DEAL_SELECT,
  }), [userId, stuckSince]))

  // Стадии счетов и согласований отбираем ПО КОДАМ через справочник (см. lib/dashboard.ts:
  // семантика стадий у типов 31 и 176 в Bitrix не расставлена, «Оплачен» и «Отчет сдан»
  // помечены как «в работе»). Пустой список = справочник не пришёл; тогда фильтр не ставим
  // и говорим об этом на экране, а не показываем весь архив как «горящее».
  const invPaidStages = useMemo(
    () => stageIdsByCode(refs.itemStageOptions(INVOICE_TYPE), INVOICE_PAID_CODES), [refs],
  )
  const repHangStages = useMemo(
    () => stageIdsByCode(refs.itemStageOptions(REPORT_TYPE), REPORT_HANG_CODES), [refs],
  )

  // Счета клиенту (31) со сроком оплаты в прошлом и НЕ на стадии «Оплачен».
  // `closedate` у смарт-процесса — это именно срок оплаты счёта
  // (`ЗНАНИЕ_из_exec_панели.md` §1.3, там он так и лёг в `due_date`). Поля нет в колонках
  // ядра, оно читается из raw — ISO-дату ядро сравнивает как дату, а не как текст.
  //
  // Стадию фильтруем в ЯДРЕ, а не на странице: тогда `total` — точное число неоплаченных
  // просроченных счетов, а не «сколько нашлось среди первых 50». Разница на живых данных
  // большая: на 28.07.2026 у одного координатора 1013 счетов с датой в прошлом всего
  // и 50 на странице.
  //
  // Запрос выключен, пока справочник стадий не пришёл (третий аргумент useItems). Без этого
  // экран сначала спрашивал ядро БЕЗ условия по стадии и на мгновение показывал у него
  // «1013» вместо «137» (на 28.07.2026) — а заодно тратил лишний запрос на каждое открытие.
  const overdueInvoices = useItems(INVOICE_TYPE, useMemo<ListParams>(() => ({
    filter: {
      assignedById: userId, '<closedate': today, '!closedate': '',
      ...(invPaidStages.length ? { '!stageId': invPaidStages } : {}),
    },
    order: { closedate: 'ASC' },
    select: [...SYSTEM_SELECT, 'closedate', 'begindate'],
  }), [userId, today, invPaidStages]), refs.loaded)

  // Согласования (176), стоящие на «человеческих» стадиях и не менявшиеся HANG_DAYS дней.
  // Это цепочка согласований, а не таблица (`БИЗНЕС-ПРОЦЕССЫ_Bitrix.md` §5): видно, что
  // запись стоит, но не видно, на ком именно — заданий бизнес-процессов ядро не отдаёт.
  const hangingReports = useItems(REPORT_TYPE, useMemo<ListParams>(() => ({
    filter: {
      assignedById: userId, '<=updatedTime': hangSince,
      ...(repHangStages.length ? { stageId: repHangStages } : {}),
    },
    order: { updatedTime: 'ASC' },
    select: [...SYSTEM_SELECT],
  }), [userId, hangSince, repHangStages]), refs.loaded)

  // ─────────────── ЗАДАНИЯ ПРОЦЕССОВ: по исполнителю задания ───────────────
  //
  // Третье измерение, и снова НЕ сводимое с двумя первыми. Деньги считаются по
  // ответственному в сделке, люди — по оргструктуре, а задание бизнес-процесса Bitrix
  // адресует КОНКРЕТНОМУ человеку (bp_tasks.user_id), и это ни то, ни другое: согласование
  // сделки уходит руководителю, которого в самой сделке нет вовсе.
  //
  // ОДИН запрос на весь блок, включая команду. На 28.07.2026 `/bp/by-user` отдаёт всех
  // 352 человек сразу и без постраничности — так решено в ядре. Спрашивать по человеку
  // значило бы 382 запроса у корня оргструктуры; ровно это и стережёт tests/nplus1.test.tsx.
  const bpЛюди = useПоЛюдям(useMemo(() => ({ old_days: BP_OLD_DAYS }), []))
  // Десять самых старых заданий лично на нём. Отдельный запрос, потому что сводка по людям
  // строк не содержит: в ней числа, а человеку нужно увидеть, ЧТО именно висит.
  const bpМои = useЗадания(useMemo(() => ({
    user: userId, state: 'live', sort: 'old', limit: HOT_ROWS,
  }), [userId]))

  const bpЯ = useMemo(() => bpЧеловек(bpЛюди.data?.rows, userId), [bpЛюди.data, userId])
  const bpСтроки = useMemo(() => bpМои.data?.rows ?? [], [bpМои.data])
  // Тот же ответ, разложенный по id: колонка «Заданий» в таблице людей своего запроса
  // не делает — иначе у корня оргструктуры вышло бы 382 обращения.
  const bpПоЛюдям = useMemo(() => bpПоId(bpЛюди.data?.rows), [bpЛюди.data])

  const stuckRows = useMemo(() => stuckDeals.data?.items ?? [], [stuckDeals.data])
  // Отбор по стадии сделало ядро, поэтому страницы фильтровать больше нечем — берём как есть.
  const invRows = useMemo(() => overdueInvoices.data?.items ?? [], [overdueInvoices.data])
  const repRows = useMemo(() => hangingReports.data?.items ?? [], [hangingReports.data])
  const invTotal = overdueInvoices.data?.total ?? 0
  const repTotal = hangingReports.data?.total ?? 0

  // Названия компаний для всех списков «горит» — ОДНИМ запросом со списком id
  // (ядро понимает его как IN). По запросу на строку это было бы 30 запросов.
  //
  // Ждём, пока все три источника id ответят. Без этого ожидания запрос уходил ДВАЖДЫ:
  // сначала по компаниям из сделок без движения, потом ещё раз — когда доехали счета.
  // Второй запрос не ошибка, но это ровно тот же список плюс пара id, и его видно в логе.
  const idsReady = !stuckDeals.isPending && !payments.isPending
    && (refs.loaded ? !overdueInvoices.isPending : true)
  const companyIds = useMemo(() => (idsReady ? [
    ...stuckRows.map((d) => d.COMPANY_ID),
    ...invRows.map((r) => refId(r.companyId)),
    ...payRows.slice(0, HOT_ROWS).map((p) => p.companyId),
  ] : []), [idsReady, stuckRows, invRows, payRows])
  const titles = useCompanyTitles(companyIds)
  const titleOf = (id: unknown) => titles.data?.[String(id ?? '')] ?? null

  const userName = refs.userName(userId)
  /**
   * Устарел ли хоть один источник денежных плиток. Отдельная переменная, а не проверка
   * у каждой плитки: под пометкой «синк отстал» половина плиток печатала «—» (счётчики
   * умели), а половина — «0 ₽» и «0 платежей». Из двух соседних утверждений человек
   * выбирает привычное: «долгов нет». Ноль здесь не итог устаревших данных, а их
   * отсутствие, и денежная плитка обязана говорить это прочерком — так же, как в
   * Client360, где то же место уже вылечено.
   */
  const деньгиУстарели = !!(openDeals.data?.stale || payments.data?.stale
    || closedMonth.data?.stale || paymentsResp.data?.stale)
  /** То же для блока «Сроки»: пустая выборка на устаревших данных — это не «ничего не горит». */
  const срокиУстарели = !!(stuckDeals.data?.stale || payments.data?.stale
    || overdueInvoices.data?.stale || hangingReports.data?.stale)
  // «Что горит» складывается из чисел РАЗНОЙ полноты: сделки без движения и просроченные
  // счета ядро считает точно (`total`), а просроченные платежи — только по загруженной
  // странице (признак «оплачен» ядро отфильтровать не может, см. lib/payments.ts).
  // Поэтому это «не меньше чем», и так и подписано.
  const hot = payTotals.buckets.overdue.count + invTotal + (stuckDeals.data?.total ?? 0)

  return (
    <>
      {/* ── Кто это по данным. Строка, ради которой существует весь экран: два измерения
             названы разными словами и показаны рядом, чтобы их не спутали. ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>{userName}</h2>
          <div className="spacer" />
          <span className="muted mono">#{userId}</span>
        </div>
        <dl className="dl">
          <dt>Числится в отделах</dt>
          <dd>
            {memberOf.length
              ? memberOf.map((d) => d.name || `#${d.id}`).join(' · ')
              : <span className="muted">
                  ни в одном (UF_DEPARTMENT не заполнен либо сотрудник уволен — состав отделов
                  ядро собирает только по действующим)
                </span>}
          </dd>
          <dt>Руководит отделами</dt>
          <dd>
            {headRoots.length
              ? (
                <>
                  {headRoots.map((d) => d.name || `#${d.id}`).join(' · ')}
                  {zone.length > headRoots.length
                    && <span className="muted"> — всего с подчинёнными: {zone.length}</span>}
                </>
              )
              : <span className="muted">
                  ни одним: в Bitrix поле «Руководитель» (UF_HEAD) ни у одного отдела на него
                  не указывает. Должность в карточке роли не играет — для системы это просто текст.
                </span>}
          </dd>
          <dt>Что горит</dt>
          <dd>
            {срокиУстарели
              ? <span className="muted">
                  неизвестно: ядро предупредило, что данные устарели. «Ничего не горит» — это
                  не то же самое, что «мы не знаем, что горит», и второе сейчас как раз наш случай.
                </span>
              : hot > 0
                ? `горящих позиций не меньше ${hot}. Просроченных платежей —`
                  + ` ${payTotals.buckets.overdue.count} (по загруженной странице);`
                  + ` неоплаченных счетов с истёкшим сроком — ${invTotal};`
                  + ` сделок без движения — ${stuckDeals.data?.total ?? 0}`
                  + ' (эти два числа ядро считает точно)'
                : <span className="muted">по загруженным страницам — ничего</span>}
          </dd>
          {/* Отдельной строкой, а не слагаемым в «Что горит». Задание процесса и разрыв
              цепочки живут в других зеркалах, считаются по-другому и с горящими сделками
              пересекаются: просроченный платёж — это и «платёж просрочен» выше, и разрыв
              payment_overdue здесь. Сложи мы их — число выросло бы вдвое на ровном месте. */}
          <dt>Ждёт его лично</dt>
          <dd>
            {bpЛюди.isLoading
              ? <span className="muted">заданий процессов: считаем…</span>
              : bpЛюди.error
                ? <span className="muted">
                    заданий процессов: ядро не ответило — это «не знаем», а не «ноль»
                  </span>
                : bpЯ
                  ? <>
                      заданий бизнес-процессов — <b>{bpЯ.tasks}</b>
                      {bpЯ.old > 0 && `, из них дольше ${BP_OLD_DAYS} дней — ${bpЯ.old}`}
                      {typeof bpЯ.oldest_days === 'number'
                        && `; самое старое ждёт ${fmtDays(bpЯ.oldest_days)}`}
                      . Это число ядро считает по всему зеркалу заданий, а не по странице.
                    </>
                  : <span className="muted">
                      заданий бизнес-процессов на нём нет — в зеркале bizproc ни одного живого
                    </span>}
          </dd>
        </dl>
        {!org.loaded && (
          <div className="notice" style={{ marginTop: 10 }}>
            Ядро не отдало оргструктуру (`departments` в /refs). Блок «Люди» показать нечем —
            это не «отделов нет», а «данных нет».
          </div>
        )}
      </div>

      {/* ─────────────── ДЕНЬГИ ─────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Деньги</h2>
          <div className="spacer" />
          <span className="hint">
            считается по ответственному в сделке и в платеже, а не по отделу
          </span>
        </div>

        {/* Выборка сужается правами роли: сотрудник, смотрящий «глазами» коллеги, видит
            не всё, что у того есть. Плитки под этой пометкой — портфель и долг, то есть
            ровно тот случай, когда часть суммы читают как всю. */}
        <QueryState isLoading={openDeals.isLoading} error={openDeals.error}
                    stale={openDeals.data?.stale} ageMin={openDeals.data?.ageMin}
                    scope={openDeals.data?.scope}>
          <div className="tiles">
            <div className="tile" title="Сделки с CLOSED=N — счётчик ядра по всей выборке">
              <div className="t-label">Открытых сделок</div>
              <div className="t-sum">{openDeals.data?.stale ? '—' : openDeals.data?.total ?? '—'}</div>
              <div className="t-label">он ответственный</div>
            </div>
            <div className="tile">
              <div className="t-label">Портфель</div>
              <div className="t-sum">{openDeals.data?.stale ? '—' : fmtMoney(portfolio.sum)}</div>
              <div className="t-label">
                {openDeals.data?.stale
                  ? 'данные устарели'
                  : `по ${portfolio.count} из ${openDeals.data?.total ?? 0} открытых`}
              </div>
            </div>
            <div className={`tile${!payments.data?.stale && payTotals.buckets.overdue.count ? ' hot' : ''}`}>
              <div className="t-label">Просрочено</div>
              <div className="t-sum">
                {payments.data?.stale ? '—' : fmtMoney(payTotals.buckets.overdue.sum)}
              </div>
              <div className="t-label">
                {payments.data?.stale
                  ? 'данные устарели'
                  : `${payTotals.buckets.overdue.count} `
                    + plural(payTotals.buckets.overdue.count, 'платёж', 'платежа', 'платежей')}
              </div>
            </div>
            <div className="tile">
              <div className="t-label">Долг</div>
              <div className="t-sum">{payments.data?.stale ? '—' : fmtMoney(payTotals.total.sum)}</div>
              <div className="t-label">
                {payments.data?.stale
                  ? 'данные устарели'
                  : `по ${payRows.length} из ${payments.data?.total ?? 0} неоплаченных`}
              </div>
            </div>
            <div className="tile" title="Стадия со смыслом «успех» по справочнику ядра">
              <div className="t-label">Выиграно за месяц</div>
              <div className="t-sum">{closedMonth.data?.stale ? '—' : fmtMoney(wonSum.sum)}</div>
              <div className="t-label">
                {closedMonth.data?.stale
                  ? 'данные устарели'
                  : `${wonRows.length} из ${closedMonth.data?.total ?? 0} закрытых`}
              </div>
            </div>
            <div className="tile">
              <div className="t-label">Закрыто за {month.label}</div>
              <div className="t-sum">{closedMonth.data?.stale ? '—' : closedMonth.data?.total ?? '—'}</div>
              <div className="t-label">сделок, включая проигранные</div>
            </div>
          </div>

          {деньгиУстарели && (
            <div className="notice" style={{ marginTop: 10 }}>
              Часть плиток показывает прочерк: ядро предупредило, что данные устарели. Прочерк
              здесь означает «правильной цифры у нас сейчас нет», а не «ноль».
            </div>
          )}

          <div className="hint" style={{ marginTop: 10 }}>
            «Открытых сделок» и «Закрыто за месяц» ядро считает по всей выборке — это точные
            числа. Портфель, долг и «выиграно» посчитаны по загруженным страницам
            (по {pluralN(PAGE, 'записи', 'записи', 'записей')}): агрегатов ядро не отдаёт, а выкачивать всю историю ради одной суммы
            нельзя. Если под плиткой написано «по N из M» и N меньше M — сумма неполная.
            {portfolio.otherCount > 0 && ` Среди открытых есть ${portfolio.otherCount} `
              + `${plural(portfolio.otherCount, 'сделка', 'сделки', 'сделок')}`
              + ` в валюте (${portfolio.otherCurrencies.join(', ')}) — в портфель они НЕ вошли:`
              + ' курса в ядре нет, а складывать рубли с евро нельзя.'}
            {' '}«За месяц» здесь — календарный месяц по дате закрытия сделки. В exec-панели
            то же слово означало отчётный месяц (пара русских enum-полей), поэтому с
            бухгалтерским отчётом эта цифра совпадать не обязана.
          </div>

          {/* Второе измерение дебиторки. Не «дополнительная плитка», а прямой ответ на вопрос
              «а точно ли это его долг» — у платежа есть отдельное поле ответственного. */}
          <div className="hint" style={{ marginTop: 10 }}>
            Дебиторка выше отобрана по <b>ответственному в карточке платежа</b>
            {' '}(assignedById). У платежа есть ещё поле «Ответственный за дебиторку», и по нему
            {' '}{userName} числится в{' '}
            <b>{paymentsResp.data?.stale ? '— (данные устарели)' : paymentsResp.data?.total ?? 0}</b>
            {' '}неоплаченных платежах
            {payRespRows.length > 0 && ` на ${fmtMoney(payRespTotals.total.sum)} по загруженной странице`}.
            {' '}Это разные наборы записей, и обе цифры настоящие: exec-панель считала долг
            по «ответственному за дебиторку», а раздел «Дебиторка» в CRM — по карточке.
            {!paymentsResp.data?.stale && (paymentsResp.data?.total ?? 0) === 0
              && (payments.data?.total ?? 0) > 0
              && ' Ноль здесь чаще всего значит «поле не заполнено», а не «долгов нет».'}
          </div>

          {payTotals.dirty > 0 && (
            <div className="hint" style={{ marginTop: 6 }}>
              На странице дебиторки {pluralN(payTotals.dirty, 'платёж', 'платежа', 'платежей')}
              с противоречивыми признаками
              оплаты (дата, флаг, воронка и стадия говорят разное) — это рассинхрон в Bitrix,
              а не ошибка расчёта.
            </div>
          )}
        </QueryState>
      </div>

      <ГдеВсталаРабота userName={userName} userId={userId} gapDays={gapDays} />

      {/* ─────────────── ЛЮДИ ─────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Люди</h2>
          <div className="spacer" />
          <span className="hint">
            считается по оргструктуре Bitrix, а не по деньгам
          </span>
        </div>

        {!org.loaded ? (
          <div className="muted">
            Оргструктуры в ответе ядра нет — состав отделов показать нечем.
          </div>
        ) : headRoots.length === 0 ? (
          <>
            <div className="notice">
              {userName} не руководит ни одним отделом — по данным Bitrix. Панель строится по
              полю «Руководитель» (UF_HEAD) у отдела, а не по должности: в карточке может быть
              написано «руководитель сервисного отдела», но для системы это текст.
              {' '}Поле заполнено у {org.withHead}
              {' '}{plural(org.withHead, 'отдела', 'отделов', 'отделов')} из {org.total} — у остальных
              {' '}{org.total - org.withHead} блок «Люди» пуст у любого сотрудника, и это
              не про него лично. Чинится в Bitrix заполнением UF_HEAD, не здесь.
            </div>
            {memberOf.length > 0 && (
              <>
                <div className="hint" style={{ margin: '10px 0' }}>
                  Ниже — отделы, в которых {userName} <b>числится</b>. Это не его команда,
                  это его место в структуре.
                </div>
                <DeptList deps={memberOf} org={org} refs={refs} />
              </>
            )}
          </>
        ) : (
          <>
            <div className="tiles">
              <div className="tile">
                <div className="t-label">Отделов в зоне</div>
                <div className="t-sum">{zone.length}</div>
                <div className="t-label">
                  {headRoots.length === zone.length
                    ? 'без подчинённых'
                    : `свои: ${headRoots.length}, подчинённые: ${zone.length - headRoots.length}`}
                </div>
              </div>
              <div className="tile">
                <div className="t-label">Людей в зоне</div>
                <div className="t-sum">{teamIds.length}</div>
                <div className="t-label">не считая самого руководителя</div>
              </div>
            </div>

            {/* Вот здесь и живёт та самая ловушка. Пишем её словами, а не оставляем
                читателю догадываться, почему у «руководителя города» пустые плитки. */}
            <div className="hint" style={{ marginTop: 10 }}>
              Цифры ниже и блок «Деньги» выше <b>не связаны</b>. Там — сделки, где ответственный
              сам {userName}; здесь — сделки его подчинённых по оргструктуре. Пустая команда при
              полном портфеле (или наоборот) — не ошибка экрана: в Bitrix отдел и «зона
              ответственности за деньги» — разные измерения без связи между собой. Живой пример:
              у руководителя сервиса СПб (замер 28.07.2026) 16 действующих сотрудников в самом
            отделе и ноль дебиторки, потому что деньги
              по Петербургу висят на координаторах чужого отдела. Складывать эти два числа нельзя.
            </div>

            {/* Компонент монтируется ТОЛЬКО когда команда есть — иначе два запроса по
                команде не отправляются вовсе. Это и есть замена «enabled», которого
                у useDeals нет. */}
            {teamIds.length > 0 ? (
              <TeamBlock teamIds={teamIds} zone={zone} refs={refs} stuckSince={stuckSince}
                         bpПоЛюдям={bpПоЛюдям} bpЖдём={bpЛюди.isPending} />
            ) : (
              <div className="muted" style={{ marginTop: 12 }}>
                В отделах зоны нет ни одного действующего сотрудника, кроме
                самого {userName}. Запросы по команде не отправлялись.
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <h2 className="subhead">Отделы в зоне</h2>
              <DeptList deps={zone} org={org} refs={refs} />
            </div>
          </>
        )}
      </div>

      {/* ─────────────── СРОКИ ─────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Сроки</h2>
          <div className="spacer" />
          <span className="hint">
            пороги: без движения &gt; {STUCK_DAYS} дн., согласование стоит &gt; {HANG_DAYS} дн.
          </span>
        </div>

        {/* «0 горящих» и «не знаем, сколько горит» — разные ответы. На устаревшей выборке
            ядро отдаёт пустой список, то есть ноль здесь получается ИЗ ОТСУТСТВИЯ данных,
            а прочитан будет как «всё в порядке». Поэтому прочерк, как у денежных плиток. */}
        <div className="tiles">
          <div className={`tile${!stuckDeals.data?.stale && (stuckDeals.data?.total ?? 0) > 0 ? ' hot' : ''}`}>
            <div className="t-label">Сделки без движения</div>
            <div className="t-sum">
              {stuckDeals.data?.stale ? '—' : stuckDeals.data?.total ?? '—'}
            </div>
            <div className="t-label">
              {stuckDeals.data?.stale ? 'данные устарели' : `не менялись с ${fmtDate(stuckSince)}`}
            </div>
          </div>
          <div className={`tile${!payments.data?.stale && payTotals.buckets.overdue.count ? ' hot' : ''}`}>
            <div className="t-label">Платежи просрочены</div>
            <div className="t-sum">
              {payments.data?.stale ? '—' : payTotals.buckets.overdue.count}
            </div>
            <div className="t-label">
              {payments.data?.stale
                ? 'данные устарели'
                : `на ${fmtMoney(payTotals.buckets.overdue.sum)}`}
            </div>
          </div>
          <div className={`tile${!overdueInvoices.data?.stale && invTotal ? ' hot' : ''}`}
               title="Счёт со сроком оплаты в прошлом и не на стадии «Оплачен» — счётчик ядра">
            <div className="t-label">Счета не оплачены в срок</div>
            <div className="t-sum">
              {overdueInvoices.data?.stale ? '—' : invPaidStages.length ? invTotal : '?'}
            </div>
            <div className="t-label">
              {overdueInvoices.data?.stale
                ? 'данные устарели'
                : invPaidStages.length
                  ? 'срок оплаты в прошлом'
                  : 'справочник стадий не пришёл'}
            </div>
          </div>
          <div className={`tile${!hangingReports.data?.stale && repTotal ? ' hot' : ''}`}
               title="Отчёт на стадии подготовки/проверки/исправления — счётчик ядра">
            <div className="t-label">Согласования стоят</div>
            <div className="t-sum">
              {hangingReports.data?.stale ? '—' : repHangStages.length ? repTotal : '?'}
            </div>
            <div className="t-label">
              {hangingReports.data?.stale
                ? 'данные устарели'
                : repHangStages.length
                  ? `дольше ${HANG_DAYS} дней`
                  : 'справочник стадий не пришёл'}
            </div>
          </div>
        </div>

        <div className="grid-2" style={{ marginTop: 12 }}>
          <div>
            <h2 className="subhead">Сделки без движения</h2>
            <QueryState isLoading={stuckDeals.isLoading} error={stuckDeals.error}
                        stale={stuckDeals.data?.stale} ageMin={stuckDeals.data?.ageMin}
                        scope={stuckDeals.data?.scope}>
              {stuckRows.length === 0 ? (
                <div className="muted">
                  {stuckDeals.data?.stale
                    ? 'Список не показан: ядро предупредило, что данные устарели. Пустота здесь'
                      + ' не означает «всё в движении» — правильного ответа у нас сейчас нет.'
                    : `Открытых сделок старше ${STUCK_DAYS} дней без изменений`
                      + ` за ${userName} нет.`}
                </div>
              ) : (
                /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
                <div className="table-wrap cards">
                  <table>
                    <thead>
                      <tr>
                        <th>Сделка</th>
                        <th className="nowrap">Стадия</th>
                        <th className="nowrap">Клиент</th>
                        <th className="num">Сумма</th>
                        <th className="num">Стоит</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stuckRows.slice(0, HOT_ROWS).map((d) => (
                        <StuckRow key={d.ID} d={d} refs={refs} title={titleOf(d.COMPANY_ID)}
                                  today={today} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(stuckDeals.data?.total ?? 0) > HOT_ROWS && (
                <div className="hint" style={{ marginTop: 8 }}>
                  Показаны {Math.min(HOT_ROWS, stuckRows.length)} самых давних
                  из {stuckDeals.data?.total}. Порог {STUCK_DAYS} дней — наше соглашение,
                  а не признак из Bitrix.
                </div>
              )}
            </QueryState>
          </div>

          <div>
            <h2 className="subhead">Счета: срок оплаты в прошлом</h2>
            <QueryState isLoading={overdueInvoices.isLoading} error={overdueInvoices.error}
                        stale={overdueInvoices.data?.stale} ageMin={overdueInvoices.data?.ageMin}
                        scope={overdueInvoices.data?.scope}>
              {invRows.length === 0 ? (
                <div className="muted">
                  {overdueInvoices.data?.stale
                    ? 'Список не показан: ядро предупредило, что данные устарели. Пустота здесь'
                      + ' не означает «просроченных счетов нет».'
                    : `Счетов со сроком оплаты в прошлом и не на стадии «Оплачен»`
                      + ` за ${userName} нет.`}
                </div>
              ) : (
                <ul className="plain-list">
                  {invRows.slice(0, HOT_ROWS).map((r) => (
                    <li key={String(r.id)}>
                      <a href={bitrixItemUrl(INVOICE_TYPE, r.id)} target="_blank" rel="noreferrer">
                        {r.title || `Счёт #${r.id}`}
                      </a>
                      <div className="hint">
                        {[
                          refId(r.companyId)
                            ? titleOf(r.companyId) || `компания #${refId(r.companyId)}`
                            : 'без компании',
                          refs.itemStageName(String(r.stageId ?? '')),
                          `срок ${fmtDate(String(r.closedate ?? ''))}`,
                          (() => {
                            const n = daysSince(r.closedate, today)
                            return n === null ? '' : `просрочен на ${fmtDays(n)}`
                          })(),
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="hint" style={{ marginTop: 8 }}>
                Отбор: смарт-процесс 31, ответственный — {userName}, поле `closedate` (срок
                оплаты счёта) раньше сегодняшнего дня, стадия — не «Оплачен». Всё три условия
                считает ядро, поэтому <b>{invTotal}</b> — точное число, а в списке первые
                {' '}{Math.min(HOT_ROWS, invRows.length)} по сроку.
                {/* Тут нельзя молчать: это единственное место экрана, где мы судим о смысле
                    по коду стадии, а не по семантике Bitrix. */}
                {' '}<b>Важно про «оплачен».</b> У типа 31 семантика стадий в Bitrix не
                расставлена: все восемь стадий помечены «в работе», включая «Оплачен».
                Поэтому оплаченность определяется по коду стадии
                {' '}({INVOICE_PAID_CODES.join(', ')}), а не по смыслу — единственное такое
                место на экране. Заведут в Bitrix девятую стадию «Оплачен банком» — сюда
                придётся дописать её код; правильное лечение — расставить семантику в Bitrix.
                {invPaidStages.length === 0 && ' Справочник стадий типа 31 сейчас не пришёл,'
                  + ' поэтому по стадии не отфильтровано вовсе: в числе есть и оплаченные счета.'}
                {' '}Ссылка ведёт в Bitrix: своей карточки счёта в CRM пока нет.
              </div>
            </QueryState>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <h2 className="subhead">Согласования, которые стоят</h2>
          <QueryState isLoading={hangingReports.isLoading} error={hangingReports.error}
                      stale={hangingReports.data?.stale} ageMin={hangingReports.data?.ageMin}
                      scope={hangingReports.data?.scope}>
            {repRows.length === 0 ? (
              <div className="muted">
                {hangingReports.data?.stale
                  ? 'Список не показан: ядро предупредило, что данные устарели. Пустота здесь'
                    + ' не означает «ничего не зависло».'
                  : 'Отчётов по сделкам (смарт-процесс 176) на стадиях подготовки, проверки или'
                    + ` исправления и без изменений дольше ${HANG_DAYS} дней за ${userName} нет.`}
              </div>
            ) : (
              <ul className="plain-list">
                {repRows.slice(0, HOT_ROWS).map((r) => (
                  <li key={String(r.id)}>
                    <a href={bitrixItemUrl(REPORT_TYPE, r.id)} target="_blank" rel="noreferrer">
                      {r.title || `Отчёт #${r.id}`}
                    </a>
                    <div className="hint">
                      {[
                        refs.itemStageName(String(r.stageId ?? '')),
                        refId(r.parentId2) ? `сделка #${refId(r.parentId2)}` : '',
                        (() => {
                          const n = daysSince(r.updatedTime, today)
                          return n === null ? '' : `не менялся ${fmtDays(n)}`
                        })(),
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {/* Честная граница знания. Раньше здесь стояло «заданий бизнес-процессов в ядре
                нет вовсе» — это устарело: зеркало bizproc появилось, и блок «Задания
                процессов» ниже как раз отвечает «на ком». Но отвечает он ПО ДРУГОМУ
                признаку — по исполнителю задания, — и сложить эти два списка нельзя. */}
            <div className="hint" style={{ marginTop: 8 }}>
              Отчёты по сделкам — это цепочка согласований из 24 шаблонов (замер 28.07.2026) бизнес-процессов
              (РОП, руководители сервиса и ремонта, проектный отдел), а не таблица. Отбор
              здесь — по ответственному В ЗАПИСИ, по дате изменения и по стадии. «На ком
              именно стоит согласование» из этих трёх признаков не следует: это знают задания
              бизнес-процессов, и они показаны отдельным блоком ниже — по исполнителю задания,
              а не по ответственному в записи. Два блока отвечают на разные вопросы и в одну
              цифру не складываются.
              {' '}Стадии взяты списком кодов ({REPORT_HANG_CODES.join(', ')} — подготовка,
              проверка у руководства, исправление ошибок, повторная проверка), потому что
              семантика у типа 176 тоже не расставлена: стадия «Отчет сдан» помечена
              «в работе» и занимает подавляющее большинство записей. Не исключи её — и
              «зависшие согласования» превратились бы в архив сданных отчётов
              {repHangStages.length > 0
                && ' (без этого условия у иного координатора «зависших» набирается'
                   + ' в десятки раз больше — весь его архив)'}.
              {repHangStages.length === 0 && ' Справочник стадий типа 176 сейчас не пришёл,'
                + ' поэтому по стадии не отфильтровано: в числе весь архив сданных отчётов.'}
            </div>
          </QueryState>
        </div>
      </div>

      {/* ─────────────── ЗАДАНИЯ ПРОЦЕССОВ ─────────────── */}
      <ЗаданияПроцессов userName={userName} люди={bpЛюди} мои={bpМои}
                        я={bpЯ} строки={bpСтроки} команда={teamIds} />

      {/* ─────────────── чего здесь нет ─────────────── */}
      <div className="card">
        <h2>Чего на этом экране нет и почему</h2>
        <ul className="plain-list">
          <li className="muted">
            <b>Единой цифры «моя зона».</b> Отдел и ответственность за деньги в Bitrix не связаны
            (`ЗНАНИЕ_из_exec_панели.md` §2.1). exec-панель решала это переключателем «по моим
            людям / по зоне доступа», где вторая половина держалась на РУЧНОМ списке
            бизнес-юнитов — и четыре руководителя из восьми видели нули из-за опечатки в
            названии. Мы такой список не заводим, пока в ядре нет справочника юнитов с id.
          </li>
          <li className="muted">
            <b>Нормы и плана.</b> В exec у каждого числа была средняя за 3 месяца («и что?»).
            Здесь её нет: ядро не считает агрегаты, а собирать три месяца истории страницами
            по {PAGE} — это сотни запросов на открытие экрана. Появится агрегат в ядре —
            появится и норма.
          </li>
          <li className="muted">
            <b>Прибыли, ФОТ и расходов.</b> Разница, налоги, ФОТ и счета поставщикам живут
            в полях, которые считают бизнес-процессы Bitrix, и в ручных таблицах exec-портала.
            Ни того, ни другого в ядре нет.
          </li>
          <li className="muted">
            <b>Кнопки «закрыть задание».</b> Сами задания теперь видны (блок выше), а закрыть
            их отсюда нельзя, и это не «пока не сделали»: `bizproc.task.complete` выполняет
            ТОЛЬКО СВОЁ задание — Bitrix ищет его среди заданий автора запроса. CRM ходит
            служебным вебхуком, поэтому чужое задание она не найдёт вовсе, а своё закрыла бы
            от имени живого человека. Нужна персональная привязка каждого сотрудника —
            отдельное решение владельца. До тех пор ссылка ведёт в Bitrix.
          </li>
          <li className="muted">
            <b>Истории закрытых заданий.</b> В зеркале bizproc полны только ЖИВЫЕ задания:
            наблюдение начали 27.07.2026, и закрытых до этого дня там нет. Поэтому «сколько он
            закрыл за месяц» посчитать нечем, и мы такой цифры не показываем.
          </li>
          <li className="muted">
            <b>Разрывов по клиенту.</b> «Где встала работа» умеет фильтр по ответственному и по
            воронке, но не по компании: у ручки ядра `/chain/gaps` такого параметра нет. Тот же
            вопрос про одного клиента задать негде — и в «Клиенте 360°» этого блока поэтому нет.
          </li>
          <li className="muted">
            <b>Правок.</b> Экран только читает. Запись в сделку и в платёж перезапускает
            бизнес-процессы, которые раздают задания живым людям, — это отдельная задача.
          </li>
        </ul>
      </div>
    </>
  )
}

// ─────────────────────────── где встала работа ───────────────────────────

/**
 * Разрывы цепочки по сделкам этого человека.
 *
 * ТОТ ЖЕ ВОПРОС «что у меня», но заданный не деньгам, а ходу работы: деньги видны только
 * там, где счёт уже выставлен, а самая дорогая остановка — когда его нет.
 *
 * ОДИН ЗАПРОС ВМЕСТО ШЕСТИ. «Работа принята, а счёта нет», «счёт есть, а карточки платежа
 * нет», «состав и сумма разошлись» — это заглядывание в шесть смарт-процессов и в состав
 * по каждой сделке. Ядро считает всё это одним проходом (`chain_api.разрывы`), и брать
 * оттуда обязательно: посчитай мы «нет счёта» здесь по-своему — у одного вопроса стало бы
 * два ответа, как это уже было с прибылью.
 *
 * ОТДЕЛЬНЫЙ КОМПОНЕНТ — из-за прав. `/chain/gaps` открыт только тем, кому видна вся база
 * (`rights.проверить_чтение_свода`): свод считается по всем сделкам сразу, и сузить его до
 * зоны нечем — это был бы ответ на другой вопрос. Роли со своей зоной получают 403, поэтому
 * запрос из такой учётки не отправляется вовсе, а на месте блока стоит объяснение. Дверь
 * настоящая всё равно на сервере; проверка здесь — способ не ходить за заведомым отказом
 * и не засорять журнал доступа. Тот же приём и та же формулировка, что в pages/Mismatches.tsx.
 */
function ГдеВсталаРабота({ userName, userId, gapDays }: {
  userName: string
  userId: string
  gapDays: number
}) {
  const права = useRightsApi()
  if (права.loaded && (права.denied('deals') || права.scoped('deals'))) {
    return <СводНеОткрыт свой={права.scoped('deals')} основание={права.basis} />
  }
  return <РазрывыБлок userName={userName} userId={userId} gapDays={gapDays} />
}

/**
 * Отказ по правам — объяснением, а не пустой таблицей. Пустота здесь читалась бы как
 * «у него ничего не встало», то есть как «всё в порядке»; это худшая неправда для блока,
 * ради которого он и заведён.
 */
function СводНеОткрыт({ свой, основание }: { свой: boolean; основание: string }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Где встала работа</h2>
      </div>
      <div className="empty">
        <div className="empty-title">Этот свод вам не открыт</div>
        <div className="empty-hint">
          {свой
            ? 'Разрывы цепочки считаются по всем сделкам сразу — сузить их до вашей зоны'
              + ' нечем: это был бы ответ на другой вопрос, и экран не смог бы сказать,'
              + ' на какой именно. Блок открыт тем, кому видна вся база.'
            : 'Раздел сделок закрыт вашей роли, а разрывы цепочки — это деньги сделок.'}
          {основание ? ` Ваша зона: ${основание}.` : ''}
          {' '}Пусто здесь означало бы «ничего не встало», и показывать пустоту вместо
          отказа мы не станем.
        </div>
      </div>
    </div>
  )
}

/** Сам блок. Рисуется только тогда, когда запрос имеет право уйти. */
function РазрывыБлок({ userName, userId, gapDays }: {
  userName: string
  userId: string
  gapDays: number
}) {
  const разрывы = useРазрывы(useMemo(() => ({
    assigned: userId, days: gapDays, limit: GAP_ROWS,
  }), [userId, gapDays]))
  const gapRows = useMemo(() => разрывы.data?.rows ?? [], [разрывы.data])
  const gapПоВидам = useMemo(() => разрывыПоВидам(gapRows), [gapRows])
  const gapВсего = разрывы.data?.total ?? 0
  /** Страница вместила всю выборку — значит числа по видам точные, а не «по N из M». */
  const gapПолно = gapRows.length >= gapВсего
  const gapНазвания = useMemo(
    () => new Map((разрывы.data?.kinds ?? []).map((k) => [k.code, k])), [разрывы.data],
  )

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Где встала работа</h2>
        <div className="spacer" />
        {/* Ссылка БЕЗ ответственного намеренно, как и у «Дебиторки» в шапке экрана:
            «Путь сделки» читает из адреса только `kind` и `days`, а `assigned` живёт в его
            локальном состоянии. Ссылка вида `?assigned=…` открыла бы список ПО ВСЕМ под
            подписью «его разрывы» — то есть тихо соврала бы. Появится параметр там —
            появится и здесь. */}
        <Link to={`/chain?days=${gapDays}`}>раздел «Путь сделки» →</Link>
      </div>

      {/* Граница данных — над цифрами, а не под ними: разрыв «счёта нет» у сделки
          2019 года не значит ничего, потому что счета в зеркале ведут с 25.04.2023.
          Окно и пороги приезжают с ответом, а не зашиты в экран. */}
      <div className="notice" style={{ marginBottom: 10 }}>
        Окно — <b>{fmtDays(разрывы.data?.window?.days ?? gapDays)}</b> по дате создания сделки
        {разрывы.data?.window?.today && `, на ${fmtDate(разрывы.data.window.today)}`}. За его
        пределами экран молчит намеренно: у каждого источника своё начало (счета в зеркале
        с 25.04.2023, АВР с 20.02.2024, платежи с 16.04.2024), и «записи нет» там означает
        «источник про те дни не знает», а не остановку работы. Порог «согласование
        зависло» — {fmtDays(разрывы.data?.window?.hang_days ?? HANG_DAYS)}, это наше соглашение.
      </div>

      <QueryState isLoading={разрывы.isLoading} error={разрывы.error}>
        <div className="tiles">
          <div className={`tile${gapВсего > 0 ? ' hot' : ''}`}
               title="Сделки, у которых порвано хоть одно звено — счётчик ядра по всей выборке">
            <div className="t-label">Сделок с разрывом</div>
            <div className="t-sum">{gapВсего}</div>
            <div className="t-label">он ответственный</div>
          </div>
          {/* Названия видов берутся ИЗ ОТВЕТА, а не из списка в этом файле. Не пришли —
              плиток нет вовсе: печатать код `no_invoice` вместо заголовка значило бы
              показать человеку внутреннее имя, а завести русские подписи здесь — развести
              вторую пару названий рядом с теми, что ядро уже пишет в «Пути сделки». */}
          {КЛЮЧЕВЫЕ_РАЗРЫВЫ.filter((код) => gapНазвания.has(код)).map((код) => {
            const вид = gapНазвания.get(код)!
            const n = gapПоВидам.get(код) ?? 0
            const деньги = разрывыРубли(gapRows, код)
            return (
              <div className={`tile${n > 0 ? ' hot' : ''}`} key={код} title={вид.why}>
                <div className="t-label">{вид.title}</div>
                <div className="t-sum">{n}</div>
                <div className="t-label">
                  {n > 0 ? `на ${fmtMoney(деньги.sum)}` : 'таких сделок нет'}
                </div>
              </div>
            )
          })}
        </div>
        {gapНазвания.size === 0 && (
          <div className="hint" style={{ marginTop: 8 }}>
            Ядро не прислало описания видов разрыва, поэтому разбивки по видам нет — только
            общее число слева. Это «нечем подписать», а не «разрывов одного вида нет».
          </div>
        )}

        {gapRows.length === 0 ? (
          <div className="empty" style={{ marginTop: 12 }}>
            <div className="empty-title">
              Разрывов по сделкам {userName} в этом окне нет
            </div>
            <div className="empty-hint">
              Проверено {fmtDays(разрывы.data?.window?.days ?? gapDays)}: счёт после принятой
              работы, карточка платежа после счёта, срок оплаты, состав против суммы.
              Это не «сделок нет» — это «в перечисленных местах цепочка не рвётся».
            </div>
          </div>
        ) : (
          /* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */
          <div className="table-wrap cards" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Сделка</th>
                  <th className="nowrap">Где встало</th>
                  <th>Что ещё порвано</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {gapRows.slice(0, HOT_ROWS).map((r) => (
                  <tr key={r.deal_id}>
                    <td data-label="Сделка">
                      <EntityLink kind="deal" id={r.deal_id} title={r.title || `#${r.deal_id}`} />
                    </td>
                    <td className="nowrap" data-label="Где встало">
                      {r.stuck_at
                        ? <span className="badge lose">{r.stuck_at.title}</span>
                        : <span className="muted">только наблюдения</span>}
                    </td>
                    <td data-label="Что ещё порвано">
                      <span className="hint">
                        {(r.gaps ?? [])
                          .filter((к) => к !== r.stuck_at?.code)
                          .map((к) => gapНазвания.get(к)?.title ?? к)
                          .join(' · ') || '—'}
                      </span>
                    </td>
                    <td className="num" data-label="Сумма">
                      {fmtMoney(r.opportunity, r.currency || 'RUB')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="hint" style={{ marginTop: 8 }}>
          <b>Сделок с разрывом — {gapВсего}</b>: это счётчик ядра по всей выборке, он точный.
          Числа на остальных плитках посчитаны по {gapRows.length}
          {' '}{plural(gapRows.length, 'загруженной строке', 'загруженным строкам',
                      'загруженным строкам')}
          {gapПолно
            ? ' — то есть по всей выборке целиком, потому что она в страницу поместилась.'
            : ` из ${gapВсего}: страница ядра ${GAP_ROWS} строк, и по видам это «не меньше чем».`}
          {' '}Что считать разрывом, решает ядро (`chain_api`) — тем же кодом, что раздел
          «Путь сделки»; свой такой же расчёт на экране дал бы два ответа на один вопрос.
          {' '}Список отсортирован ядром по сумме вниз: начинать надо с дорогого, а не
          со свежего. В таблице {pluralN(Math.min(HOT_ROWS, gapRows.length),
            'самая дорогая сделка', 'самые дорогие сделки', 'самых дорогих сделок')}.
          {' '}Ссылка выше ведёт в «Путь сделки» БЕЗ фильтра по ответственному: раздел
          держит его в своём состоянии, а не в адресе — там фамилию придётся выбрать руками.
        </div>

        {разрывы.data?.data_notes && (
          <ul className="hint" style={{ marginTop: 8, paddingLeft: 18 }}>
            {Object.entries(разрывы.data.data_notes).map(([к, т]) => <li key={к}>{т}</li>)}
          </ul>
        )}
      </QueryState>
    </div>
  )
}

// ─────────────────────────── задания процессов ───────────────────────────

/**
 * «Что висит лично на нём и на его людях» — задания бизнес-процессов Bitrix.
 *
 * ТРЕТЬЕ ИЗМЕРЕНИЕ, и оно не сводится с двумя предыдущими. Деньги считаются по
 * ответственному в сделке, люди — по оргструктуре, а задание процесса адресовано
 * КОНКРЕТНОМУ исполнителю (`bp_tasks.user_id`). Согласование сделки уходит руководителю,
 * которого в самой сделке нет, — и наоборот, ответственный за сделку может не иметь по ней
 * ни одного задания. Складывать «горящие сделки» и «висящие задания» нельзя.
 *
 * СВОИХ ЗАПРОСОВ КОМПОНЕНТ НЕ ДЕЛАЕТ: и сводка по людям, и десяток самых старых заданий уже
 * получены в DashboardBody — сводка ОДНИМ запросом сразу на всех, включая команду.
 *
 * ЧИСЛА ЗДЕСЬ ТОЧНЫЕ, в отличие от соседних блоков: `/bp/by-user` — это агрегат ядра по
 * всему зеркалу заданий, без постраничности. Это сказано на экране словами, иначе человек
 * перенесёт на них оговорку «по N из M», которая стоит у денежных плиток.
 */
function ЗаданияПроцессов({ userName, люди, мои, я, строки, команда }: {
  userName: string
  люди: ReturnType<typeof useПоЛюдям>
  мои: ReturnType<typeof useЗадания>
  я: BpПоЛюдям | undefined
  строки: ЗаданиеСтрока[]
  команда: string[]
}) {
  const права = useRightsApi()
  const итогКоманды = useMemo(() => bpИтогПоЛюдям(люди.data?.rows, команда), [люди.data, команда])
  const порог = люди.data?.old_days ?? BP_OLD_DAYS

  // Раздел закрыт роли — говорим об этом словами. Пустой блок читался бы как «заданий нет».
  if (права.denied('bp-tasks')) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="page-head" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Задания процессов</h2>
        </div>
        <div className="empty">
          <div className="empty-title">Раздел «Задания процессов» вашей роли недоступен</div>
          <div className="empty-hint">
            Это отказ по правам, а не «заданий нет»: числа существуют, но показывать их вашей
            учётной записи нельзя.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Задания процессов</h2>
        <div className="spacer" />
        {/* Как и у «Пути сделки»: раздел держит фильтр по человеку в своём состоянии,
            а не в адресе, поэтому `?user=` здесь был бы обманом. */}
        <Link to="/bp-tasks">раздел «Задания процессов» →</Link>
      </div>

      {/* Граница данных: у зеркала bizproc она другая, чем у сделок, и молчать об этом
          нельзя. Живые задания полны, закрытых до 27.07.2026 нет вовсе. */}
      <div className="notice" style={{ marginBottom: 10 }}>
        Живым считается задание с признаком «ждёт» (user_status = 0), возраст — <b>от старта
        процесса</b>, а не от последнего касания задания. Закрытые задания в зеркале неполны:
        наблюдение за ними начали 27.07.2026, и «сколько он закрыл за месяц» посчитать нечем.
      </div>

      <QueryState isLoading={люди.isLoading} error={люди.error}>
        <div className="tiles">
          <div className={`tile${(я?.tasks ?? 0) > 0 ? ' hot' : ''}`}
               title="Живые задания, адресованные лично ему — агрегат ядра по всему зеркалу">
            <div className="t-label">Заданий на нём</div>
            <div className="t-sum">{я?.tasks ?? 0}</div>
            <div className="t-label">точное число, не по странице</div>
          </div>
          <div className={`tile${(я?.old ?? 0) > 0 ? ' hot' : ''}`}>
            <div className="t-label">Висят дольше {порог} дн.</div>
            <div className="t-sum">{я?.old ?? 0}</div>
            <div className="t-label">порог наш, а не Bitrix</div>
          </div>
          <div className="tile">
            <div className="t-label">Самое старое</div>
            <div className="t-sum">
              {typeof я?.oldest_days === 'number' ? fmtDays(я.oldest_days) : '—'}
            </div>
            <div className="t-label">
              {typeof я?.median_days === 'number'
                ? `середина списка — ${fmtDays(я.median_days)}`
                : 'заданий нет'}
            </div>
          </div>
          <div className="tile">
            <div className="t-label">Сделок задето</div>
            <div className="t-sum">{я?.deals ?? 0}</div>
            <div className="t-label">
              {(я?.deals ?? 0) > 0
                ? 'у остальных заданий сделки нет'
                : 'ни одно задание не привязано к сделке'}
            </div>
          </div>
          {команда.length > 0 && (
            <>
              <div className={`tile${итогКоманды.tasks > 0 ? ' hot' : ''}`}
                   title="Сумма счётчиков ядра по подчинённым — тоже точное число">
                <div className="t-label">На его людях</div>
                <div className="t-sum">{итогКоманды.tasks}</div>
                <div className="t-label">
                  {итогКоманды.people > 0
                    ? `на ${итогКоманды.people} из `
                      + pluralN(команда.length, 'человека', 'человека', 'человек')
                    : 'ни на ком'}
                </div>
              </div>
              <div className={`tile${итогКоманды.leftTasks > 0 ? ' hot' : ''}`}
                   title="Задания на отключённых учётках: их не закроет никто">
                <div className="t-label">Из них на уволенных</div>
                <div className="t-sum">{итогКоманды.leftTasks}</div>
                <div className="t-label">
                  {итогКоманды.leftPeople > 0
                    ? `${итогКоманды.leftPeople} ` +
                      plural(итогКоманды.leftPeople, 'учётка', 'учётки', 'учёток')
                    : 'таких нет'}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="hint" style={{ marginTop: 10 }}>
          Все числа выше — <b>счётчики ядра по всему зеркалу заданий</b>, а не по загруженной
          странице: «по N из M» здесь нет намеренно, потому что M и N совпадают. Считает их тот
          же запрос, что показывает раздел «Задания процессов», — своего расчёта на этом экране
          нет. Одно задание может ждать нескольких человек сразу (массовое ознакомление), и
          тогда оно попадёт в счётчик каждому: это не двойной учёт, а честный ответ на вопрос
          «сколько висит на нём».
          {итогКоманды.leftTasks > 0 && ' Задания на отключённых учётках не закроет никто:'
            + ' процесс по ним стоит навсегда, и чинится это в Bitrix, а не здесь.'}
        </div>
      </QueryState>

      <div style={{ marginTop: 12 }}>
        <h2 className="subhead">Что висит дольше всего</h2>
        <QueryState isLoading={мои.isLoading} error={мои.error}>
          {строки.length === 0 ? (
            <div className="empty">
              <div className="empty-title">Живых заданий процессов на {userName} нет</div>
              <div className="empty-hint">
                Это ответ по зеркалу bizproc, а не по стадиям смарт-процессов: там видно, что
                запись стоит, а здесь — на ком именно. Пусто значит «ни одного задания ему
                не адресовано».
              </div>
            </div>
          ) : (
            <ul className="plain-list">
              {строки.map((r) => {
                const вид = видЗадания(r.activity)
                const ждёт = видОжидания(r.days_waiting, порог)
                const url = ссылкаВBitrix(r.document_url)
                return (
                  <li key={r.row_key}>
                    {url
                      ? <a href={url} target="_blank" rel="noreferrer">{r.name || `Задание #${r.bitrix_id}`}</a>
                      : (r.name || `Задание #${r.bitrix_id}`)}
                    <div className="hint">
                      <span className={вид.класс}>{вид.ярлык}</span>
                      {' '}
                      <span className={ждёт.класс}>{ждёт.ярлык}</span>
                      {' · '}
                      {[
                        r.template_name || 'шаблон не назван',
                        r.deal_id ? `сделка #${r.deal_id}` : названиеДокумента(r.doc_kind, r.doc_type_id),
                        (r.waiting_people ?? 1) > 1
                          ? 'ждёт ещё '
                            + pluralN((r.waiting_people ?? 1) - 1,
                                      'человека', 'человека', 'человек')
                          : '',
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
          <div className="hint" style={{ marginTop: 8 }}>
            В списке {pluralN(Math.min(HOT_ROWS, строки.length),
              'самое старое задание', 'самых старых задания', 'самых старых заданий')}
            {' '}из {мои.data?.total ?? строки.length}; порядок — по старту процесса,
            снизу вверх.
            {' '}Ссылка ведёт в Bitrix: закрыть задание из CRM нельзя вовсе — Bitrix выполняет
            только СВОЁ задание, а CRM ходит служебным вебхуком и чужое не найдёт.
            {' '}«Просрочено» на этом экране не показано намеренно: настоящий срок Bitrix
            проставляет редко, и наша оценка «висит дольше {fmtDays(порог)}» — это не срок,
            назначенный процессом.
          </div>
        </QueryState>
      </div>
    </div>
  )
}

// ─────────────────────────── мелкие куски разметки ───────────────────────────

function StuckRow({ d, refs, title, today }: {
  d: Deal
  refs: ReturnType<typeof useRefs>
  title: string | null
  today: string
}) {
  const idle = idleDays(d, today)
  return (
    <tr>
      <td data-label="Сделка"><EntityLink kind="deal" id={d.ID} title={d.TITLE || `#${d.ID}`} /></td>
      <td className="nowrap" data-label="Стадия">
        <span className={`badge ${refs.stageKind(d.STAGE_ID)}`}>{refs.stageName(d.STAGE_ID)}</span>
      </td>
      <td className="nowrap" data-label="Клиент">
        <EntityLink kind="company" id={d.COMPANY_ID} title={title} empty="—" />
      </td>
      <td className="num" data-label="Сумма">{fmtMoney(d.OPPORTUNITY, d.CURRENCY_ID || 'RUB')}</td>
      <td className="num" data-label="Стоит">{idle === null ? '—' : fmtDays(idle)}</td>
    </tr>
  )
}

/**
 * Команда: сколько у подчинённых открытых сделок и где затыки.
 *
 * Ровно ДВА запроса к ядру на всю команду — со списком id в фильтре (ядро понимает его
 * как IN). Не по запросу на человека: 25 подчинённых дали бы 25 запросов, а у корня
 * оргструктуры 382 подчинённых — это 382. Проверено на живом ядре 28.07.2026: список
 * из 382 id ядро принимает одним запросом (2 948 открытых сделок).
 *
 * Компонент рисуется только при непустой команде — так «нет команды» не превращается
 * в запрос с чужим фильтром (см. комментарий в DashboardBody).
 */
function TeamBlock({ teamIds, zone, refs, stuckSince, bpПоЛюдям, bpЖдём }: {
  teamIds: string[]
  zone: RefDepartment[]
  refs: ReturnType<typeof useRefs>
  stuckSince: string
  /** Сводка `/bp/by-user`, разложенная по id. Своего запроса колонка «Заданий» НЕ делает. */
  bpПоЛюдям: Map<string, BpПоЛюдям>
  /** Сводка ещё едет: «0 заданий» до её прихода читалось бы как «заданий нет». */
  bpЖдём: boolean
}) {
  const teamOpen = useDeals(useMemo<ListParams>(() => ({
    filter: { ASSIGNED_BY_ID: teamIds, CLOSED: 'N' },
    order: { DATE_MODIFY: 'DESC' },
    select: DEAL_SELECT,
  }), [teamIds]))

  // Затыки команды отдельным запросом: `total` тогда точный — это главное число блока,
  // а распределение по людям всё равно считается по странице.
  const teamStuck = useDeals(useMemo<ListParams>(() => ({
    filter: { ASSIGNED_BY_ID: teamIds, CLOSED: 'N', '<=DATE_MODIFY': stuckSince },
    order: { DATE_MODIFY: 'ASC' },
    select: DEAL_SELECT,
  }), [teamIds, stuckSince]))

  const openRows = useMemo(() => teamOpen.data?.items ?? [], [teamOpen.data])
  const stuckRows = useMemo(() => teamStuck.data?.items ?? [], [teamStuck.data])
  const openByUser = useMemo(() => countByAssignee(openRows), [openRows])
  const stuckByUser = useMemo(() => countByAssignee(stuckRows), [stuckRows])

  return (
    <>
      <div className="tiles" style={{ marginTop: 12 }}>
        <div className="tile" title="Счётчик ядра по всей выборке команды">
          <div className="t-label">Их открытых сделок</div>
          <div className="t-sum">{teamOpen.data?.stale ? '—' : teamOpen.data?.total ?? '—'}</div>
          <div className="t-label">по ответственному в сделке</div>
        </div>
        <div className={`tile${(teamStuck.data?.total ?? 0) > 0 ? ' hot' : ''}`}>
          <div className="t-label">Из них без движения</div>
          <div className="t-sum">{teamStuck.data?.stale ? '—' : teamStuck.data?.total ?? '—'}</div>
          <div className="t-label">дольше {STUCK_DAYS} дней</div>
        </div>
      </div>

      <QueryState isLoading={teamOpen.isLoading} error={teamOpen.error}
                  stale={teamOpen.data?.stale} ageMin={teamOpen.data?.ageMin}
                  scope={teamOpen.data?.scope}>
        <div className="table-wrap cards" style={{ marginTop: 12 }}>
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Отдел</th>
                <th className="num">Открытых</th>
                <th className="num">Без движения</th>
                {/* Колонка из ДРУГОГО зеркала и с другой полнотой: числа сделок слева
                    посчитаны по загруженной странице, а задания — по всему зеркалу bizproc.
                    Поэтому у человека может стоять «0 сделок» и «14 заданий» одновременно,
                    и это не противоречие. Сказано под таблицей. */}
                <th className="num">Заданий БП</th>
              </tr>
            </thead>
            <tbody>
              <TeamRows teamIds={teamIds} zone={zone} refs={refs}
                        openByUser={openByUser} stuckByUser={stuckByUser}
                        bpПоЛюдям={bpПоЛюдям} bpЖдём={bpЖдём} />
            </tbody>
          </table>
        </div>
      </QueryState>

      <div className="hint" style={{ marginTop: 10 }}>
        Числа «Открытых» и «Без движения» в строках — <b>по загруженным страницам</b>:
        {' '}{openRows.length} из {teamOpen.data?.total ?? 0} открытых сделок команды
        и {stuckRows.length} из {teamStuck.data?.total ?? 0} застоявшихся. Ядро отдаёт
        по {PAGE} записей и группировать не умеет, поэтому у человека в конце списка может
        стоять 0 при непустой работе — итоги на плитках точные, распределение по людям нет.
        Состав отделов ядро собирает только по действующим сотрудникам: уволенные остаются
        ответственными в старых сделках, но в этой таблице их нет.
        {' '}Колонка <b>«Заданий БП»</b> устроена иначе: это счётчик ядра по всему зеркалу
        заданий, а не по странице, — и потому она точная. Из-за разной полноты у человека
        бывает «0 открытых» и непустые задания сразу: сделки его в первые {PAGE} записей не
        попали, а задания посчитаны все. Сложить две эти колонки нельзя ещё и по смыслу:
        слева — сделки, где он ответственный, справа — задания, адресованные лично ему.
      </div>
    </>
  )
}

/**
 * Строки таблицы людей. Своих запросов НЕ делает: и открытые сделки, и затыки уже
 * посчитаны в TeamBlock одной парой запросов на всю команду.
 */
function TeamRows({ teamIds, zone, refs, openByUser, stuckByUser, bpПоЛюдям, bpЖдём }: {
  teamIds: string[]
  zone: RefDepartment[]
  refs: ReturnType<typeof useRefs>
  openByUser: Map<string, number>
  stuckByUser: Map<string, number>
  bpПоЛюдям: Map<string, BpПоЛюдям>
  bpЖдём: boolean
}) {
  const deptOf = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const d of zone) {
      for (const u of d.users) {
        const key = String(u)
        const list = m.get(key)
        if (list) list.push(d.name || `#${d.id}`)
        else m.set(key, [d.name || `#${d.id}`])
      }
    }
    return m
  }, [zone])

  const rows = useMemo(() => [...teamIds]
    .map((id) => ({
      id,
      name: refs.userName(id),
      open: openByUser.get(id) ?? 0,
      stuck: stuckByUser.get(id) ?? 0,
      bp: bpПоЛюдям.get(id)?.tasks ?? 0,
      bpOld: bpПоЛюдям.get(id)?.old ?? 0,
      dept: (deptOf.get(id) ?? []).join(' · '),
    }))
    // Сначала те, у кого затыки, потом задания, потом сделки — руководитель смотрит
    // сверху вниз. Задания в ключе сортировки не «важнее» сделок: они просто точные,
    // и человек с висящими заданиями не должен проваливаться в скрытый хвост из-за того,
    // что его сделки не попали в первые PAGE записей.
    .sort((a, b) => b.stuck - a.stuck || b.bpOld - a.bpOld || b.open - a.open
      || b.bp - a.bp || a.name.localeCompare(b.name, 'ru')),
  [teamIds, refs, openByUser, stuckByUser, bpПоЛюдям, deptOf])

  /**
   * Показываем только тех, у кого на загруженных страницах хоть что-то есть, и обрезаем
   * длинный хвост.
   *
   * Почему. На 28.07.2026 у корня оргструктуры 382 подчинённых, а страница ядра — 50
   * сделок, и в неё попадают всего 22 человека: 360 строк из 382 были бы «0 и 0». Такая
   * таблица не сообщает «у них нет работы» (это неправда) — она сообщает «их сделки не
   * попали в первые 50». Прятать ноль как «нет данных» честнее, чем печатать три сотни
   * нулей: количество спрятанных написано под таблицей.
   */
  const shown = useMemo(() => {
    // Задания в условии «есть что показать» участвуют наравне со сделками: человек с
    // четырнадцатью висящими заданиями и без сделок на первой странице — это как раз тот,
    // кого руководитель ищет, и прятать его в хвост было бы обратным ответу.
    const active = rows.filter((r) => r.open > 0 || r.stuck > 0 || r.bp > 0)
    return (active.length > 0 ? active : rows).slice(0, TEAM_ROWS)
  }, [rows])

  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={5} className="empty">
          <div className="empty-title">
            В отделах зоны нет ни одного действующего сотрудника, кроме самого руководителя.
          </div>
        </td>
      </tr>
    )
  }

  return (
    <>
      {shown.map((r) => (
        <tr key={r.id}>
          <td data-label="Сотрудник">{r.name}</td>
          <td className="muted" data-label="Отдел">{r.dept || '—'}</td>
          <td className="num" data-label="Открытых">
            {r.open || <span className="muted">0</span>}
          </td>
          <td className="num" data-label="Без движения">
            {r.stuck ? <span className="badge lose">{r.stuck}</span> : <span className="muted">0</span>}
          </td>
          <td className="num" data-label="Заданий БП">
            {/* Прочерк, пока сводка едет: «0» рядом с непустыми соседними колонками
                прочитался бы как «заданий на нём нет», а мы этого ещё не знаем. */}
            {bpЖдём
              ? <span className="muted">—</span>
              : r.bpOld
                ? <span className="badge lose" title={`из них дольше порога: ${r.bpOld}`}>{r.bp}</span>
                : r.bp || <span className="muted">0</span>}
          </td>
        </tr>
      ))}
      {rows.length > shown.length && (
        <tr>
          <td colSpan={5} className="hint">
            Остальные {rows.length - shown.length} не показаны: ни сделок на загруженных
            страницах, ни живых заданий процессов у них не нашлось. Про задания это точный
            ответ, про сделки — нет: они могли не попасть в первые {PAGE} записей.
          </td>
        </tr>
      )}
    </>
  )
}

/** Список отделов с руководителем и числом людей. Никаких запросов — всё из /refs. */
function DeptList({ deps, org, refs }: {
  deps: RefDepartment[]
  org: ReturnType<typeof makeOrg>
  refs: ReturnType<typeof useRefs>
}) {
  if (deps.length === 0) return <div className="empty"><div className="empty-title">Отделов нет</div></div>
  return (
    <ul className="plain-list">
      {deps.map((d) => {
        const path = org.pathOf(d.id).slice(0, -1).map((p) => p.name || `#${p.id}`)
        return (
          <li key={d.id}>
            {d.name || `#${d.id}`}
            {' '}
            <span className="muted mono">#{d.id}</span>
            <div className="hint">
              {[
                path.length ? path.join(' → ') : 'корень структуры',
                `людей: ${d.users.length}`,
                d.head_user_id
                  ? `руководитель: ${refs.userName(d.head_user_id)}`
                  : 'руководитель не указан (UF_HEAD пуст)',
              ].join(' · ')}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
